import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, Copy } from "lucide-react";
import { API, authHeader } from "@/lib/api";

const PLATFORMS = ["Instagram", "Facebook", "TikTok", "YouTube", "X / Twitter"];
const TONES = ["Heartfelt", "Inspiring", "Urgent", "Grateful", "Casual"];

export const AdminCaption = () => {
  const [prompt, setPrompt] = useState("");
  const [platform, setPlatform] = useState("Instagram");
  const [tone, setTone] = useState("Heartfelt");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe the post first."); return; }
    setLoading(true);
    setResult("");
    try {
      const { data } = await axios.post(
        `${API}/admin/caption`,
        { prompt, platform, tone },
        { headers: authHeader() }
      );
      setResult(data.caption);
    } catch {
      toast.error("Couldn't generate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(result)
      .then(() => toast.success("Caption copied."))
      .catch(() => toast.error("Copy failed — select and copy manually."));
  };

  return (
    <section data-testid="admin-caption" className="border border-[#1a1a1a] bg-[#0a0a0a] p-6 md:p-8 mb-14">
      <h2 className="font-display uppercase text-3xl text-white flex items-center gap-3 mb-6">
        <Sparkles className="text-green" size={24} /> AI caption writer
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5 mb-5">
        <div>
          <label className="label block mb-2">Platform</label>
          <select data-testid="caption-platform" className="field !border-b" value={platform} onChange={(e) => setPlatform(e.target.value)}>
            {PLATFORMS.map((p) => <option key={p} value={p} className="bg-black">{p}</option>)}
          </select>
        </div>
        <div>
          <label className="label block mb-2">Tone</label>
          <select data-testid="caption-tone" className="field !border-b" value={tone} onChange={(e) => setTone(e.target.value)}>
            {TONES.map((t) => <option key={t} value={t} className="bg-black">{t}</option>)}
          </select>
        </div>
      </div>

      <label className="label block mb-2">What's the post about?</label>
      <textarea
        data-testid="caption-prompt"
        className="field"
        rows={2}
        placeholder="e.g. handed out 60 hot meals and winter coats under the bridge last night"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
      />

      <button
        data-testid="caption-generate"
        onClick={generate}
        disabled={loading}
        className="btn-fill mt-6 border border-green text-green font-sub font-semibold text-lg px-8 py-3.5 rounded-full flex items-center gap-2 disabled:opacity-60"
      >
        {loading ? <Loader2 className="animate-spin" size={20} /> : <Sparkles size={18} />}
        {loading ? "Writing…" : "Generate caption"}
      </button>

      {result && (
        <div data-testid="caption-result" className="mt-6 border border-[#1a1a1a] bg-[#050505] p-5 relative">
          <p className="font-sub text-zinc-200 whitespace-pre-wrap leading-relaxed">{result}</p>
          <button data-testid="caption-copy" onClick={copy} className="mt-4 flex items-center gap-2 border border-green text-green btn-fill px-4 py-2 rounded-full font-sub text-sm">
            <Copy size={15} /> Copy
          </button>
        </div>
      )}
    </section>
  );
};
